import { useState, useEffect } from 'react';
import { axiosInstance } from '@/axiosConfig'; // Certifique-se que aponta para sua API
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem,
} from '@/components/ui/command';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Check, ChevronsUpDown, Landmark } from 'lucide-react';
import { BANCOS_BRASIL } from '@/lib/bancos';

const TIPOS_CONTA = [
  "Conta Corrente",
  "Conta Poupança",
  "Conta Salário",
  "Caixa Interno",
  "Conta Investimento"
];

const initialContaForm = {
  nome: '', 
  nome_banco: '',
  tipo_conta: 'Conta Corrente',
  agencia: '',
  numero_conta: '',
  chave_pix: ''
};

export default function ContasBancarias() {
  const [contas, setContas] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estados do Modal/Formulário
  const [openConta, setOpenConta] = useState(false);
  const [editingContaId, setEditingContaId] = useState(null);
  const [contaForm, setContaForm] = useState(initialContaForm);
  const [openBancoPopover, setOpenBancoPopover] = useState(false);

  // Carregar dados
  const loadContas = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/contas');
      setContas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar contas bancárias.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContas();
  }, []);

  // Submit (Criar ou Editar)
  const handleContaSubmit = async (e) => {
    e.preventDefault();
    
    // Adicione este log para confirmar que o botão foi clicado
    console.log("Enviando dados:", contaForm); 

    try {
      if (editingContaId) {
        // USE 'contaForm' DIRETAMENTE AQUI
        await axiosInstance.put(`/contas/${editingContaId}`, contaForm);
        toast.success('Conta atualizada com sucesso!');
      } else {
        // E AQUI TAMBÉM
        await axiosInstance.post('/contas', contaForm);
        toast.success('Conta criada com sucesso!');
      }
      
      setOpenConta(false);
      setEditingContaId(null);
      setContaForm(initialContaForm);
      loadContas();
    } catch (error) {
      console.error(error); // Log do erro real no console
      const msg = error.response?.data?.detail || 'Erro ao salvar conta.';
      toast.error(msg);
    }
  };

  // Abrir modal de edição
  const handleEditConta = (conta) => {
    setEditingContaId(conta.id);
    setContaForm({
      nome: conta.nome,
      nome_banco: conta.nome_banco,
      tipo_conta: conta.tipo_conta,
      agencia: conta.agencia || '',
      numero_conta: conta.numero_conta || '',
      chave_pix: conta.chave_pix || ''
    });
    setOpenConta(true);
  };

  // Deletar conta
  const handleDeleteConta = async (id) => {
    try {
      await axiosInstance.delete(`/contas/${id}`);
      toast.success('Conta removida!');
      loadContas();
    } catch (error) {
      toast.error('Erro ao deletar conta.');
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      {/* Cabeçalho */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2 pt-8">
            <Landmark className="h-8 w-8 text-blue-600" />
            Contas Bancárias
          </h1>
          <p className="text-gray-500 mt-1">Gerencie suas contas bancárias e caixas internos.</p>
        </div>
        <Button
          onClick={() => {
            setEditingContaId(null);
            setContaForm(initialContaForm);
            setOpenConta(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Conta
        </Button>
      </div>

      {/* Lista de Contas */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {contas.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Landmark className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Nenhuma conta cadastrada.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Apelido</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Banco</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Dados</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Chave PIX</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contas.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{c.nome}</div>
                      <div className="text-xs text-gray-500">{c.tipo_conta}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {c.nome_banco}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {(c.agencia || c.numero_conta) ? (
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-400">Ag / Conta</span>
                          <span>{c.agencia || '-'} / {c.numero_conta || '-'}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">--</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {c.chave_pix ? (
                        <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs select-all">
                          {c.chave_pix}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic text-xs">Não cadastrada</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleEditConta(c)} className="mr-2 hover:bg-blue-50 hover:text-blue-600">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="hover:bg-red-50 hover:text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir Conta</AlertDialogTitle>
                            <AlertDialogDescription>
                              Tem certeza que deseja excluir <strong>{c.nome}</strong>? Essa ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteConta(c.id)} className="bg-red-600 hover:bg-red-700">
                              Confirmar Exclusão
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* === MODAL DE CADASTRO/EDIÇÃO === */}
      <Dialog open={openConta} onOpenChange={setOpenConta}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-blue-600" />
              {editingContaId ? 'Editar Conta' : 'Nova Conta Bancária'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados abaixo para gerenciar sua conta bancária.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleContaSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Apelido da Conta <span className="text-red-500">*</span></Label>
              <Input 
                value={contaForm.nome} 
                onChange={(e) => setContaForm({ ...contaForm, nome: e.target.value })} 
                placeholder="Ex: Nubank Principal" 
                required 
              />
            </div>
            
            <div className="space-y-2">
              <Label>Instituição Financeira <span className="text-red-500">*</span></Label>
              <Popover open={openBancoPopover} onOpenChange={setOpenBancoPopover}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={openBancoPopover} className="w-full justify-between font-normal">
                    {contaForm.nome_banco || "Selecione o banco..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[450px] p-0">
                  <Command>
                    <CommandInput placeholder="Pesquisar banco..." />
                    <CommandEmpty>Nenhum banco encontrado.</CommandEmpty>
                    <CommandGroup className="max-h-[200px] overflow-y-auto">
                      {BANCOS_BRASIL.map((banco) => (
                        <CommandItem
                          key={banco}
                          value={banco}
                          onSelect={(currentValue) => {
                            setContaForm({ ...contaForm, nome_banco: currentValue });
                            setOpenBancoPopover(false);
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${contaForm.nome_banco === banco ? "opacity-100" : "opacity-0"}`} />
                          {banco}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={contaForm.tipo_conta} onValueChange={(val) => setContaForm({ ...contaForm, tipo_conta: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_CONTA.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Agência</Label>
                <Input value={contaForm.agencia} onChange={(e) => setContaForm({ ...contaForm, agencia: e.target.value })} placeholder="0000" />
              </div>
              <div className="space-y-2">
                <Label>Conta</Label>
                <Input value={contaForm.numero_conta} onChange={(e) => setContaForm({ ...contaForm, numero_conta: e.target.value })} placeholder="00000-0" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Chave Pix Padrão</Label>
              <Input value={contaForm.chave_pix} onChange={(e) => setContaForm({ ...contaForm, chave_pix: e.target.value })} placeholder="CPF, CNPJ, Email ou Aleatória" />
            </div>

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setOpenConta(false)}>Cancelar</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {editingContaId ? 'Salvar Alterações' : 'Criar Conta'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}